import React, { useEffect, useState } from 'react';
import { Form, FormCheck, FormControl, FormGroup, FormLabel } from 'react-bootstrap';
import { faFloppyDisk } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useFleet } from '../../hooks/useFleet';
import { useEventTypes } from '../../hooks/useEventTypes';
import { useInput } from '../../hooks/useInput';
import { EventType } from '../../interfaces';
import { MatchMode, PermittedPlate, PostPermittedPlate, PriviledgedGroup } from '../../interfaces/whitelisted_plate';
import { invalidatePermittedPlates, useBackwardsCompatiblePlates, usePermittedPlatesGroups } from '../../queries';
import { PermittedPlatesService } from '../../services';
import { getDefaultState, hash, humanizeEnum } from '../../utils';
import { matchModeLabels } from './matchModeLabels';
import { Alert } from '../atoms/alert';
import { Button } from '../atoms/button';
import { ButtonTag } from '../atoms/buttonTag';
import { ActionButtonsContainer } from '../atoms/buttonsContainer';
import { ChevronDown } from '../atoms/chevrons';
import { H3, H4 } from '../atoms/headings';
import { Hr } from '../atoms/hr';
import { Modal } from '../atoms/modal';
import { Padded } from '../atoms/padded';
import { ProgressButton } from '../atoms/progressButton';
import { DatalistInput } from '../atoms/datalist';
import { ValidationError } from '../atoms/validationError';
import { EventTypeDropdown } from '../insights/atoms/EventTypeDropdown';
import { RegionSelector } from './RegionSelector';
import { DropdownTrigger, PlateInputColumn, PlateMatchingRow, RadioRow } from './atoms';

interface Props {
  toggle: () => void;
  plate?: PermittedPlate | PostPermittedPlate | null;
}

function selectedEventTypesFromPlate(
    plate: PermittedPlate | PostPermittedPlate | null | undefined,
    allEventTypes: EventType[],
): EventType[] {
  if (!plate || allEventTypes.length === 0) {
    return [];
  }

  if (plate.event_type_identifiers && plate.event_type_identifiers.length > 0) {
    return plate.event_type_identifiers
        .map((identifier) => allEventTypes.find((et) => et.identifier === identifier))
        .filter((et): et is EventType => et != null);
  }

  if ('event_type_identifier' in plate && String(plate.event_type_identifier) === '*') {
    return [...allEventTypes];
  }

  if ('event_type_identifier' in plate && plate.event_type_identifier) {
    const found = allEventTypes.find((et) => et.identifier === plate.event_type_identifier);

    return found ? [found] : [];
  }

  return [];
}

export function AddPlateModal(props: Props) {
  const { toggle } = props;
  const [isSaving, setSaving] = useState(false);
  const fleet = useFleet();
  const { data } = useBackwardsCompatiblePlates();
  const exceptions = data ?? [];
  const groups = usePermittedPlatesGroups();
  const eventTypes = useEventTypes();
  const [selectedEventTypes, setSelectedEventTypes] = useState<EventType[]>([]);
  const [licensePlateInteractive, setLicensePlateInteractive] = useState(false);

  useEffect(() => {
    setSelectedEventTypes(selectedEventTypesFromPlate(props.plate, eventTypes));
  }, [props.plate?.permitted_plate_id, hash(eventTypes)]);

  useEffect(() => {
    setLicensePlateInteractive(false);
  }, [props.plate?.permitted_plate_id]);

  const MODAL_STYLE = { minWidth: '35vw' };

  const { bind: plate } = useInput<string>(props.plate?.license_plate || '');
  const { bind: state } = useInput<string>(props.plate?.state_code || getDefaultState().abbr);
  const { bind: group } = useInput<string>(props.plate?.categorization_code || '');
  const { bind: matchMode } = useInput<MatchMode>(props.plate?.match_mode || 'exact');

  const plateFieldInvalid =
    licensePlateInteractive && plate.value.trim().length === 0;

  const isSelected = (id: number) => selectedEventTypes.some((s) => s.id === id);
  const allSelected = eventTypes.length > 0 && eventTypes.every((et) => isSelected(et.id));

  const toggleEventType = (et: EventType) => {
    if (isSelected(et.id)) {
      setSelectedEventTypes(selectedEventTypes.filter((s) => s.id !== et.id));
    } else {
      setSelectedEventTypes([...selectedEventTypes, et]);
    }
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedEventTypes([]);
    } else {
      setSelectedEventTypes([...eventTypes]);
    }
  };

  const getEventTypeLabel = () => {
    if (selectedEventTypes.length === 0) {
      return 'Select all that apply';
    }
    if (allSelected) {
      return 'All event types';
    }
    if (selectedEventTypes.length === 1) {
      return selectedEventTypes[0].name
        || humanizeEnum(selectedEventTypes[0].identifier ?? '');
    }

    return `${selectedEventTypes.length} event types selected`;
  };

  const save = (evt: React.FormEvent<HTMLFormElement>): boolean => {
    evt.preventDefault();
    evt.stopPropagation();

    const newPlate: PostPermittedPlate = {
      permitted_plate_id: props.plate?.permitted_plate_id,
      license_plate: plate.value.toUpperCase(),
      state_code: state.value.toUpperCase(),
      categorization_code: group.value as PriviledgedGroup,
      event_type_identifiers: selectedEventTypes.map((et) => et.identifier),
      match_mode: matchMode.value,
    };

    if (newPlate.license_plate.trim().length < 3) {
      new Alert('License plate number must be at least 3 characters long.').alert();
      return false;
    }

    if (!newPlate.categorization_code) {
      new Alert('Please select a privileged group.').alert();
      return false;
    }

    if (selectedEventTypes.length === 0) {
      new Alert('Please select at least one event type.').alert();
      return false;
    }

    if (
      exceptions.some(
        (e) =>
          e.permitted_plate_id !== props.plate?.permitted_plate_id &&
          e.license_plate.toUpperCase() === newPlate.license_plate.toUpperCase() &&
          e.state_code.toUpperCase() === newPlate.state_code.toUpperCase() &&
          e.match_mode === newPlate.match_mode,
      )
    ) {
      const mode = matchModeLabels[newPlate.match_mode ?? 'exact'];
      new Alert(
        `License ${newPlate.license_plate} (${newPlate.state_code}, ${mode} match) is already on the permitted list!`,
      ).alert();
      return false;
    }

    setSaving(true);

    PermittedPlatesService.createOrUpdate(newPlate, fleet.uuid)
      .then(() => {
        invalidatePermittedPlates(fleet.uuid);
        setSaving(false);
        props.toggle();
      })
      .finally(() => {
        setSaving(false);
      });

    return false;
  };

  return (
    <Modal isOpen onToggle={toggle} data-test="addPlateModal" contentLabel="License plate exception" style={MODAL_STYLE}>
      <Padded>
        <H3>{props.plate ? 'Edit a ' : 'Add a new '}license plate exception</H3>
        <H4>* Indicates a required field</H4>
      </Padded>

      <Hr />

      <Form onSubmit={save} noValidate>
        <Padded>
          <FormGroup>
            <PlateMatchingRow>
              <div>
                <FormLabel>Plate matching</FormLabel>
                <RadioRow>
                  {Object.entries(matchModeLabels).map(([value, label]) => (
                    <FormCheck
                      key={value}
                      type="radio"
                      id={`match-mode-${value}`}
                      name="matchMode"
                      label={label}
                      value={value}
                      checked={matchMode.value === value}
                      onChange={matchMode.onChange}
                      inline
                    />
                  ))}
                </RadioRow>
              </div>
              <PlateInputColumn>
                <FormLabel>
                  License plate number*
                  {plateFieldInvalid && (
                    <ValidationError id="plate-error">This is a required field</ValidationError>
                  )}
                </FormLabel>
                <FormControl
                  {...plate}
                  placeholder="e.g., HEO-001, IAJ-999"
                  autoFocus
                  required
                  minLength={3}
                  onChange={(e) => {
                    setLicensePlateInteractive(true);
                    plate.onChange?.(e);
                  }}
                  onBlur={(e) => {
                    setLicensePlateInteractive(true);
                    plate.onBlur?.(e);
                  }}
                  isInvalid={plateFieldInvalid}
                  aria-invalid={plateFieldInvalid}
                  aria-describedby={plateFieldInvalid ? 'plate-error' : undefined}
                  style={{ textTransform: plate.value.length > 0 ? 'uppercase' : 'none' }}
                />
              </PlateInputColumn>
            </PlateMatchingRow>
          </FormGroup>

          <FormGroup>
            <FormLabel>State*</FormLabel>
            <RegionSelector value={state.value} onSelect={(region) => state.setValue(region)} />
          </FormGroup>

          <FormGroup>
            <FormLabel>Limit Exception to these event types only</FormLabel>
            <EventTypeDropdown
              hiddenEventTypes={eventTypes}
              isSelected={isSelected}
              onToggle={toggleEventType}
              showDots={false}
              header=""
              wrapperStyle={{ width: '100%' }}
              menuStyles={{ left: 0, right: 'auto' }}
              renderTrigger={(triggerProps) => (
                <DropdownTrigger {...triggerProps}>
                  {getEventTypeLabel()}
                  <ChevronDown />
                </DropdownTrigger>
              )}
              selectAll={{ label: 'Select all', active: allSelected, onToggle: toggleSelectAll }}
            />

            {selectedEventTypes.map((et) => (
              <ButtonTag
                key={et.id}
                onClick={() => setSelectedEventTypes(selectedEventTypes.filter((s) => s.id !== et.id))}
              >
                {humanizeEnum(et.identifier)} ×
              </ButtonTag>
            ))}
          </FormGroup>

          <FormGroup>
            <FormLabel>Privileged group</FormLabel>
            <DatalistInput {...group} placeholder="Privileged group" required minLength={3} list="groups" />

            <datalist id="groups">
              {groups.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </datalist>
          </FormGroup>

          <ActionButtonsContainer>
            <Button variant="secondary" onClick={toggle}>
              Cancel
            </Button>
            <ProgressButton variant="primary" type="submit" data-accesskey="Enter" data-disableKey isLoading={isSaving}>
              <FontAwesomeIcon icon={faFloppyDisk} style={{ marginRight: 6 }} aria-hidden="true" />Save & Close
            </ProgressButton>
          </ActionButtonsContainer>
        </Padded>
      </Form>
    </Modal>
  );
}
